import { useContext } from 'react';
import { useParams } from 'react-router-dom';

import { Stack } from '@cast/design-system';
import {
  CastwarePolicyTooltip,
  RightsizingHpaSettingsTabChips,
  useWorkloadHasLegacyHpaSettings,
  WorkloadDetailsContext,
  workloadHasHpaV2Settings,
} from '@cast/split-me/features/cluster/workload-optimization';

import { InventoryViewTabs } from '../_components/InventoryViewTabs';
import { WORKLOAD_VIEWS } from '../_constants';
import { useInventoryWorkloadsViewLocation } from '../_hooks/useInventoryWorkloadsViewLocation';
import { buildInventoryWorkloadViewPath } from '../navigation';

const HorizontalAutoscalingTabLabel = () => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { workloadHasLegacyHpaSettings } = useWorkloadHasLegacyHpaSettings({
    workload,
  });

  // Tabs sit outside the rightsizing form outlet; use workload for chip state.
  // Live form missing-state is still reflected inside the HPA settings banner.
  const isHpaSettingsMissingOnWorkloadForm =
    !!workload && !workloadHasHpaV2Settings(workload);

  return (
    <Stack direction="row" gap={8} alignItems="center">
      Horizontal autoscaling
      <RightsizingHpaSettingsTabChips
        workload={workload}
        isHpaSettingsMissingOnWorkloadForm={isHpaSettingsMissingOnWorkloadForm}
        workloadHasLegacyHpaSettings={workloadHasLegacyHpaSettings}
      />
    </Stack>
  );
};

export const InventoryWorkloadTabs = () => {
  const { clusterId, workloadId } = useParams<{
    clusterId: string;
    workloadId: string;
  }>();
  const { category } = useInventoryWorkloadsViewLocation();
  const { workload } = useContext(WorkloadDetailsContext);
  const isCastware = !!workload?.verticalScalingPolicy?.isCastware;

  if (!clusterId || !workloadId) {
    return null;
  }

  return (
    <InventoryViewTabs
      testId="workload-view-tabs"
      items={[
        {
          value: WORKLOAD_VIEWS.DASHBOARD,
          label: 'Dashboard',
          pathname: buildInventoryWorkloadViewPath({
            clusterId,
            workloadId,
            category,
            view: WORKLOAD_VIEWS.DASHBOARD,
          }),
          testId: 'workload-dashboard-tab',
        },
        {
          value: WORKLOAD_VIEWS.LIST,
          label: 'Tables',
          pathname: buildInventoryWorkloadViewPath({
            clusterId,
            workloadId,
            category,
            view: WORKLOAD_VIEWS.LIST,
          }),
          testId: 'workload-tables-tab',
        },
        {
          value: WORKLOAD_VIEWS.VERTICAL_AUTOSCALING,
          label: 'Vertical autoscaling',
          pathname: buildInventoryWorkloadViewPath({
            clusterId,
            workloadId,
            view: WORKLOAD_VIEWS.VERTICAL_AUTOSCALING,
          }),
          testId: 'workload-vertical-autoscaling-tab',
        },
        {
          value: WORKLOAD_VIEWS.HORIZONTAL_AUTOSCALING,
          label: <HorizontalAutoscalingTabLabel />,
          pathname: buildInventoryWorkloadViewPath({
            clusterId,
            workloadId,
            view: WORKLOAD_VIEWS.HORIZONTAL_AUTOSCALING,
          }),
          testId: 'workload-horizontal-autoscaling-tab',
          disabled: isCastware,
          wrap: isCastware
            ? (tab) => (
                <CastwarePolicyTooltip isCastware>
                  <div>{tab}</div>
                </CastwarePolicyTooltip>
              )
            : undefined,
        },
        {
          value: WORKLOAD_VIEWS.EVENTS,
          label: 'Events',
          pathname: buildInventoryWorkloadViewPath({
            clusterId,
            workloadId,
            view: WORKLOAD_VIEWS.EVENTS,
          }),
          testId: 'workload-events-tab',
        },
        {
          value: WORKLOAD_VIEWS.META,
          label: 'Metadata',
          pathname: buildInventoryWorkloadViewPath({
            clusterId,
            workloadId,
            view: WORKLOAD_VIEWS.META,
          }),
          testId: 'workload-meta-tab',
        },
      ]}
    />
  );
};
